/* eslint-disable */
import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "../lib/api";
import { Input } from "../components/ui/input";
import { Button } from "../components/ui/button";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import { Badge } from "../components/ui/badge";
import { BookUser, Search, MapPin, Mail, Phone, Building2, Briefcase, Users, ChefHat, Sparkles, ShieldAlert, Plus, Eye, EyeOff, Trash2 } from "lucide-react";
import ContactDetailPanel from "../components/ContactDetailPanel";
import { Label } from "../components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "../components/ui/dialog";
import { toast } from "sonner";

// Phase 98g — Carnet d'adresses central (vue lecture agrégée).
// Regroupe distributeurs AUTOMATE + revendeurs + clients B2C + traiteurs + prospects
// en une seule liste filtrable. L'édition reste dans les modules d'origine (deep-links).

const ROLE_META = {
  AUTOMATE: { label: "Automate", icon: Building2, color: "bg-emerald-100 text-emerald-800 border-emerald-300" },
  REVENDEUR: { label: "Revendeur B2B", icon: Briefcase, color: "bg-amber-100 text-amber-900 border-amber-300" },
  B2C: { label: "Client B2C", icon: Users, color: "bg-blue-100 text-blue-800 border-blue-300" },
  TRAITEUR: { label: "Traiteur", icon: ChefHat, color: "bg-fuchsia-100 text-fuchsia-800 border-fuchsia-300" },
  PROSPECT: { label: "Prospect", icon: Sparkles, color: "bg-slate-100 text-slate-700 border-slate-300" },
  COURRIER: { label: "Courrier", icon: BookUser, color: "bg-orange-100 text-orange-800 border-orange-300" },
  AUTRE: { label: "Autre", icon: BookUser, color: "bg-neutral-100 text-neutral-700 border-neutral-300" },
};

export default function Contacts() {
  const [data, setData] = useState({ items: [], stats: { by_role: {}, total: 0 } });
  const [loading, setLoading] = useState(false);
  const [role, setRole] = useState("");
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [selectedContact, setSelectedContact] = useState(null);
  // Phase 101du.6 — Création manuelle contact autonome
  const [newContactOpen, setNewContactOpen] = useState(false);
  const emptyNew = { name: "", organization: "", email: "", phone: "", address: "", postal_code: "", city: "", country: "Suisse", notes: "" };
  const [newContact, setNewContact] = useState(emptyNew);
  const [savingNew, setSavingNew] = useState(false);
  // Phase 101ev — Masquer / démasquer contacts du carnet
  const [showHiddenPanel, setShowHiddenPanel] = useState(false);
  const [hiddenContacts, setHiddenContacts] = useState([]);
  const [hidingKey, setHidingKey] = useState(null);

  const hideContact = async (c, e) => {
    e?.stopPropagation?.();
    if (!window.confirm(`Masquer « ${c.name || c.email || "ce contact"} » du carnet ?\n\nLes données sources (revendeur, distributeur, devis…) restent intactes — seule la ligne disparaît du carnet. Vous pourrez le démasquer via « Contacts masqués ».`)) return;
    setHidingKey(c.name || c.email);
    try {
      await api.post("/contacts/hide", {
        email: c.email || "",
        name: c.name || "",
        reason: "manuel via carnet",
      });
      toast.success(`« ${c.name || c.email} » masqué du carnet`);
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Erreur");
    } finally {
      setHidingKey(null);
    }
  };

  const loadHidden = async () => {
    try {
      const { data } = await api.get("/contacts/hidden");
      setHiddenContacts(data.items || []);
    } catch { setHiddenContacts([]); }
  };

  const unhideContact = async (h) => {
    try {
      const qs = new URLSearchParams();
      if (h.email) qs.set("email", h.email);
      if (h.name) qs.set("name", h.name);
      await api.delete(`/contacts/hide?${qs.toString()}`);
      toast.success("Contact remis dans le carnet");
      loadHidden();
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Erreur");
    }
  };

  // Debounce recherche
  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(search), 250);
    return () => clearTimeout(t);
  }, [search]);

  const load = async () => {
    setLoading(true);
    try {
      const qs = new URLSearchParams();
      if (role) qs.set("role", role);
      if (debouncedSearch) qs.set("search", debouncedSearch);
      const { data } = await api.get(`/contacts?${qs.toString()}`);
      setData(data);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  const saveNewContact = async () => {
    if (!newContact.name && !newContact.organization && !newContact.email) {
      toast.error("Renseignez au moins nom, organisation ou email");
      return;
    }
    setSavingNew(true);
    try {
      await api.post("/contacts/standalone", newContact);
      toast.success("Contact ajouté au carnet");
      setNewContactOpen(false);
      setNewContact(emptyNew);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur");
    } finally {
      setSavingNew(false);
    }
  };

  useEffect(() => { load(); }, [role, debouncedSearch]);

  const totalStats = data.stats || { by_role: {}, total: 0 };
  const items = data.items || [];

  const roleButtons = useMemo(() => [
    { v: "", label: "Tous", count: totalStats.total },
    { v: "AUTOMATE", label: "Automates", count: totalStats.by_role?.AUTOMATE ?? 0 },
    { v: "REVENDEUR", label: "Revendeurs", count: totalStats.by_role?.REVENDEUR ?? 0 },
    { v: "B2C", label: "B2C", count: totalStats.by_role?.B2C ?? 0 },
    { v: "TRAITEUR", label: "Traiteurs", count: totalStats.by_role?.TRAITEUR ?? 0 },
    { v: "PROSPECT", label: "Prospects", count: totalStats.by_role?.PROSPECT ?? 0 },
    { v: "COURRIER", label: "Courriers", count: totalStats.by_role?.COURRIER ?? 0 },
    { v: "AUTRE", label: "Autres", count: totalStats.by_role?.AUTRE ?? 0 },
  ], [totalStats]);

  return (
    <div className="p-6 space-y-5" data-testid="contacts-page">
      {/* Header */}
      <div className="flex items-center justify-between gap-4 flex-wrap">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-md bg-[#1F4E3D] flex items-center justify-center">
            <BookUser className="w-5 h-5 text-white" />
          </div>
          <div>
            <h1 className="text-2xl font-heading font-semibold text-[#1A1C1A]">Carnet d'adresses</h1>
            <p className="text-sm text-[#6B6E6B]">
              Vue agrégée — cliquez une ligne pour éditer, ajouter des notes ou tracer les livraisons HACCP
            </p>
          </div>
        </div>
        <div className="flex items-center gap-3">
          <Button
            onClick={() => { setNewContact(emptyNew); setNewContactOpen(true); }}
            size="sm"
            className="gap-2 bg-[#1F4E3D] hover:bg-[#173B2E]"
            data-testid="contacts-new-btn"
          >
            <Plus className="w-4 h-4" /> Nouveau contact
          </Button>
          <Button
            onClick={() => { setShowHiddenPanel(true); loadHidden(); }}
            size="sm"
            variant="outline"
            className="gap-2"
            data-testid="contacts-hidden-btn"
          >
            <EyeOff className="w-4 h-4" /> Contacts masqués
          </Button>
          <Link to="/haccp/lot-trace" data-testid="haccp-lot-trace-link">
            <Button variant="outline" size="sm" className="gap-2">
              <ShieldAlert className="w-4 h-4 text-[#C05C47]" /> Tracer un lot
            </Button>
          </Link>
          <div className="text-right">
            <div className="text-2xl font-mono font-semibold text-[#1A1C1A]">{totalStats.total}</div>
            <div className="text-xs uppercase tracking-wider text-[#6B6E6B]">
              Contacts uniques {totalStats.multi_role_count > 0 && `· ${totalStats.multi_role_count} multi-rôles`}
            </div>
          </div>
        </div>
      </div>

      {/* Filtres */}
      <div className="flex items-center gap-2 flex-wrap">
        {roleButtons.map((r) => (
          <Button
            key={r.v || "all"}
            variant={role === r.v ? "default" : "outline"}
            size="sm"
            onClick={() => setRole(r.v)}
            data-testid={`filter-role-${r.v || "all"}`}
            className="gap-2"
          >
            {r.label}
            <Badge variant="secondary" className="ml-1 text-[10px]">{r.count}</Badge>
          </Button>
        ))}
        <div className="flex-1 min-w-[220px] max-w-md relative">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-[#6B6E6B]" />
          <Input
            placeholder="Rechercher nom, email, ville, téléphone..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-9"
            data-testid="contacts-search-input"
          />
        </div>
      </div>

      {/* Tableau */}
      <div className="bg-white border border-[#E5E5E0] rounded-md overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow className="bg-[#F5F5F0]">
              <TableHead>Nom / Organisation</TableHead>
              <TableHead>Rôles</TableHead>
              <TableHead>Contact</TableHead>
              <TableHead>Ville</TableHead>
              <TableHead className="text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading ? (
              <TableRow><TableCell colSpan={5} className="text-center py-8 text-[#6B6E6B]">Chargement…</TableCell></TableRow>
            ) : items.length === 0 ? (
              <TableRow><TableCell colSpan={5} className="text-center py-8 text-[#6B6E6B]">Aucun contact trouvé</TableCell></TableRow>
            ) : items.map((c, idx) => (
              <TableRow
                key={idx}
                data-testid={`contact-row-${idx}`}
                className="cursor-pointer hover:bg-[#FAFAF7]"
                onClick={() => setSelectedContact(c)}
              >
                <TableCell>
                  <div className="font-medium text-[#1A1C1A]">{c.name || "—"}</div>
                  {c.organization && (
                    <div className="text-xs text-[#6B6E6B] mt-0.5">{c.organization}</div>
                  )}
                </TableCell>
                <TableCell>
                  <div className="flex flex-wrap gap-1">
                    {(c.roles || []).map((r) => {
                      const meta = ROLE_META[r] || { label: r, color: "bg-gray-100 text-gray-700" };
                      const Icon = meta.icon;
                      // Phase 101ew — Suffixe état activation pour les revendeurs
                      const isPendingReseller = r === "REVENDEUR" && c.reseller_activation_state === "invited";
                      const isNoInviteReseller = r === "REVENDEUR" && c.reseller_activation_state === "no_invite";
                      return (
                        <span
                          key={r}
                          className={`inline-flex items-center gap-1 px-2 py-0.5 rounded text-[11px] font-medium border ${meta.color}`}
                          title={
                            isPendingReseller ? "Lien d'activation envoyé, en attente de clic" :
                            isNoInviteReseller ? "Aucun lien d'activation envoyé" :
                            r === "REVENDEUR" ? "Compte activé" : meta.label
                          }
                        >
                          {Icon && <Icon className="w-3 h-3" />}
                          {meta.label}
                          {isPendingReseller && (
                            <span className="ml-0.5 text-[9px] text-[#8C6B2F] font-semibold">· en attente</span>
                          )}
                          {isNoInviteReseller && (
                            <span className="ml-0.5 text-[9px] text-[#575A57] font-semibold">· non invité</span>
                          )}
                          {r === "REVENDEUR" && c.reseller_activation_state === "activated" && (
                            <span className="ml-0.5 text-[9px] text-[#1F4E3D]">✓</span>
                          )}
                        </span>
                      );
                    })}
                  </div>
                </TableCell>
                <TableCell>
                  {c.email && (
                    <div className="flex items-center gap-1.5 text-sm text-[#1A1C1A]">
                      <Mail className="w-3 h-3 text-[#6B6E6B]" />
                      <a href={`mailto:${c.email}`} className="hover:underline">{c.email}</a>
                    </div>
                  )}
                  {c.phone && (
                    <div className="flex items-center gap-1.5 text-xs text-[#6B6E6B] mt-0.5">
                      <Phone className="w-3 h-3" />
                      <a href={`tel:${c.phone}`} className="hover:underline">{c.phone}</a>
                    </div>
                  )}
                </TableCell>
                <TableCell>
                  {(c.city || c.postal_code) && (
                    <div className="flex items-center gap-1.5 text-sm">
                      <MapPin className="w-3 h-3 text-[#6B6E6B]" />
                      <span>{[c.postal_code, c.city].filter(Boolean).join(" ")}</span>
                    </div>
                  )}
                </TableCell>
                <TableCell className="text-right">
                  <div className="flex items-center justify-end gap-1">
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={(e) => { e.stopPropagation(); setSelectedContact(c); }}
                      data-testid={`open-contact-${idx}`}
                      className="h-7 px-2"
                    >
                      Détail
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={(e) => hideContact(c, e)}
                      disabled={hidingKey === (c.name || c.email)}
                      title="Masquer du carnet (non-destructif)"
                      data-testid={`hide-contact-${idx}`}
                      className="h-7 w-7 p-0 text-[#8C6B2F] hover:bg-[#FBF4E9]"
                    >
                      <EyeOff className="w-3.5 h-3.5" />
                    </Button>
                  </div>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      {/* Panneau détail */}
      <ContactDetailPanel
        contact={selectedContact}
        open={Boolean(selectedContact)}
        onClose={() => setSelectedContact(null)}
        onSaved={load}
      />

      {/* Dialog Nouveau contact autonome (Phase 101du.6) */}
      <Dialog open={newContactOpen} onOpenChange={setNewContactOpen}>
        <DialogContent className="max-w-lg" data-testid="new-contact-dialog">
          <DialogHeader>
            <DialogTitle>Nouveau contact au carnet</DialogTitle>
            <DialogDescription>
              Contact autonome (hébergeur partenaire, service officiel, contact occasionnel…).
              Apparaîtra dans le carnet avec le rôle « Autre ». Éditable ensuite en cliquant sur la ligne.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="mb-1 block text-xs">Nom du contact *</Label>
                <Input value={newContact.name} onChange={(e) => setNewContact({ ...newContact, name: e.target.value })} placeholder="ex. Jean Dupont" data-testid="new-contact-name" />
              </div>
              <div>
                <Label className="mb-1 block text-xs">Organisation</Label>
                <Input value={newContact.organization} onChange={(e) => setNewContact({ ...newContact, organization: e.target.value })} placeholder="ex. Piscine Sion SA" data-testid="new-contact-org" />
              </div>
              <div>
                <Label className="mb-1 block text-xs">Email</Label>
                <Input type="email" value={newContact.email} onChange={(e) => setNewContact({ ...newContact, email: e.target.value })} data-testid="new-contact-email" />
              </div>
              <div>
                <Label className="mb-1 block text-xs">Téléphone</Label>
                <Input value={newContact.phone} onChange={(e) => setNewContact({ ...newContact, phone: e.target.value })} data-testid="new-contact-phone" />
              </div>
            </div>
            <div>
              <Label className="mb-1 block text-xs">Adresse</Label>
              <Input value={newContact.address} onChange={(e) => setNewContact({ ...newContact, address: e.target.value })} placeholder="Rue et numéro" data-testid="new-contact-address" />
            </div>
            <div className="grid grid-cols-3 gap-3">
              <div>
                <Label className="mb-1 block text-xs">NPA</Label>
                <Input value={newContact.postal_code} onChange={(e) => setNewContact({ ...newContact, postal_code: e.target.value })} data-testid="new-contact-postal" />
              </div>
              <div className="col-span-2">
                <Label className="mb-1 block text-xs">Ville</Label>
                <Input value={newContact.city} onChange={(e) => setNewContact({ ...newContact, city: e.target.value })} data-testid="new-contact-city" />
              </div>
            </div>
            <div>
              <Label className="mb-1 block text-xs">Notes</Label>
              <textarea value={newContact.notes} onChange={(e) => setNewContact({ ...newContact, notes: e.target.value })} className="w-full border border-[#E5E5E0] rounded p-2 text-sm h-16" data-testid="new-contact-notes" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setNewContactOpen(false)}>Annuler</Button>
            <Button onClick={saveNewContact} disabled={savingNew} className="bg-[#1F4E3D] hover:bg-[#173B2E]" data-testid="new-contact-save">
              {savingNew ? "…" : "Ajouter"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Dialog Contacts masqués (Phase 101ev) */}
      <Dialog open={showHiddenPanel} onOpenChange={setShowHiddenPanel}>
        <DialogContent className="max-w-2xl" data-testid="hidden-contacts-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <EyeOff className="w-4 h-4 text-[#8C6B2F]" /> Contacts masqués
            </DialogTitle>
            <DialogDescription>
              Ces contacts ne s'affichent plus dans le carnet — mais leurs données sources
              (revendeur, distributeur, devis…) restent intactes. Cliquez sur « Réintégrer »
              pour les faire réapparaître.
            </DialogDescription>
          </DialogHeader>
          {hiddenContacts.length === 0 ? (
            <div className="text-sm text-[#6B6E6B] py-6 text-center italic">
              Aucun contact masqué actuellement.
            </div>
          ) : (
            <div className="space-y-2 max-h-96 overflow-y-auto">
              {hiddenContacts.map((h) => (
                <div
                  key={h.dedup_key}
                  className="flex items-center justify-between gap-2 border border-[#E5E5E0] rounded p-2 bg-[#FAFAF7]"
                  data-testid={`hidden-row-${h.dedup_key}`}
                >
                  <div className="min-w-0">
                    <div className="text-sm font-medium truncate">{h.name || "(sans nom)"}</div>
                    <div className="text-xs text-[#6B6E6B] truncate">
                      {h.email || "—"}
                      {h.reason && <span className="ml-2 italic">· {h.reason}</span>}
                    </div>
                  </div>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => unhideContact(h)}
                    className="gap-1 shrink-0"
                    data-testid={`unhide-${h.dedup_key}`}
                  >
                    <Eye className="w-3.5 h-3.5" /> Réintégrer
                  </Button>
                </div>
              ))}
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowHiddenPanel(false)}>Fermer</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Note pied de page */}
      <div className="text-xs text-[#6B6E6B] leading-relaxed">
        <strong>Astuce.</strong> Cliquez une ligne pour ouvrir la fiche : édition inline (nom / mail / tél / adresse),
        notes internes libres, et pour les revendeurs : historique HACCP complet (lots livrés + DLC + quantités).
      </div>
    </div>
  );
}
